// 本文件被所有API请求调用，负责动态获取API基础地址和统一的API路径配置
// BASE_URL 只保留主机名和端口，不带协议
const getBaseUrl = (): string => {
  const win =
    typeof window !== "undefined" ? (window as Record<string, any>) : undefined;
  if (win && win.__ENV__ && win.__ENV__.VUE_APP_BASE_URL) {
    return win.__ENV__.VUE_APP_BASE_URL;
  }
  // 兼容本地开发环境，注意这里不要带 http://
  return process.env.VUE_APP_BASE_URL || "localhost:5000";
};

// 动态获取当前访问的子域名
const getCurrentSubdomain = (): string => {
  if (typeof window === "undefined") return "msg";
  const host = window.location.host;
  const parts = host.split(".");
  if (parts.length >= 3) {
    return parts[0]; // 返回子域名部分
  }
  return "msg"; // 默认返回msg
};

// 导出API基础地址，供全局使用
export const BASE_URL = getBaseUrl();

// 动态获取当前协议（http: 或 https:）
const getProtocol = (): string => {
  const win =
    typeof window !== "undefined" ? (window as Record<string, any>) : undefined;
  if (win && win.__ENV__ && win.__ENV__.VUE_APP_API_PROTOCOL) {
    return win.__ENV__.VUE_APP_API_PROTOCOL + ":";
  }
  return process.env.VUE_APP_API_PROTOCOL
    ? process.env.VUE_APP_API_PROTOCOL + ":"
    : typeof window !== "undefined"
    ? window.location.protocol
    : "http:";
};

const getWsProtocol = (): string => {
  const win =
    typeof window !== "undefined" ? (window as Record<string, any>) : undefined;
  if (win && win.__ENV__ && win.__ENV__.VUE_APP_WS_PROTOCOL) {
    return win.__ENV__.VUE_APP_WS_PROTOCOL + "://";
  }
  const protocol = getProtocol();
  return protocol === "https:" ? "wss://" : "ws://";
};

const protocol = getProtocol();
const wsProtocol = getWsProtocol();

// API版本和路径常量 - 统一管理所有API路径
export const API_VERSION = "v1";
export const API_BASE_PATH = `/api/${API_VERSION}`;

// 完整的API基础URL
export const API_BASE_URL = protocol + "//" + BASE_URL + API_BASE_PATH;

// 统一的API路径配置
export const API_PATHS = {
  // 认证相关
  LOGIN: "/auth/login",
  LOGOUT: "/auth/logout",
  USER_INFO: "/auth/userinfo",

  // 设备相关
  DEVICES: "/devices",
  DEVICE_MONTHLY_REMAINING: (id: string) => `/devices/${id}/monthly-remaining`,
  DEVICE_CLEAR_WIFI: (id: string) => `/devices/${id}/clear_wifi`,
  DEVICE_FORCE_4G: (id: string) => `/devices/${id}/force_4g`,
  DEVICE_FORCE_RESTART: (id: string) => `/devices/${id}/force_restart`,
  DEVICE_NETWORK_MODE: (id: string) => `/devices/${id}/network_mode`,
  DEVICE_RESTART_REASON: (id: string) => `/devices/${id}/restart_reason`,
  DEVICE_BY_ID: (id: string) => `/devices/${id}`,
  DEVICE_REGISTER: "/devices/register",
  DEVICE_ACTIVATE: "/devices/activate",
  DEVICE_STATUS: (id: string) => `/devices/${id}/status`,

  // 事件相关
  EVENTS: "/events",
  EVENT_BY_ID: (id: string) => `/events/${id}`,

  // 仪表盘相关
  DASHBOARD: "/dashboard",
  DASHBOARD_STATS: "/dashboard/stats",
  DASHBOARD_EVENTS: "/dashboard/events",
  DASHBOARD_HOTSPOTS: "/dashboard/hotspots",

  // SIM卡管理相关
  SIM_CARDS: "/sim-cards",
  SIM_CARDS_STATS: "/sim-cards/stats",
  SIM_CARDS_LIST: "/sim-cards/list",
  SIM_CARDS_BILLING_GROUPS: "/sim-cards/billing-groups",
  SIM_CARD_DETAIL: (iccid: string) => `/sim-cards/detail/${iccid}`,
  SIM_CARD_USAGE: (iccid: string) => `/sim-cards/usage/${iccid}`,
  SIM_CARD_SYNC: (iccid: string) => `/sim-cards/sync/${iccid}`,

  // OTA升级管理相关
  OTA: "/ota",
  OTA_FIRMWARE: "/ota/firmware",
  OTA_FIRMWARE_UPLOAD: "/ota/firmware/upload",
  OTA_DEVICES: "/ota/devices",
  OTA_UPGRADE: "/ota/upgrade",
  OTA_AUDIO: "/ota/audio",
  OTA_AUDIO_UPLOAD: (audioType: string) => `/ota/audio/${audioType}/upload`,

  // 其他模块路径可在此扩展...

  // 用户与个人资料
  USERS: "/users",
  USER_BY_ID: (id: number | string) => `/users/${id}`,
  USER_ME_PROFILE: "/users/me/profile",
  USER_ME_PASSWORD: "/users/me/password",

  TENANTS: "/tenants/",
  TENANT_BY_ID: (id: number | string) => `/tenants/${id}`,
  TENANT_APIKEY_CREATE: (tenantId: number | string) =>
    `/tenants/${tenantId}/apikey`,
  TENANT_APIKEYS: (tenantId: number | string) => `/tenants/${tenantId}/apikeys`,
  TENANT_APIKEY_STATUS: (
    tenantId: number | string,
    apiKeyId: number | string
  ) => `/tenants/${tenantId}/${apiKeyId}/api-status`,
  TENANT_APIKEY_DELETE: (tenantId: number | string, keyId: number | string) =>
    `/tenants/${tenantId}/apikeys/${keyId}`,
};

// API URL构建帮助函数
export const buildApiUrl = (path: string): string => {
  return API_BASE_URL + path;
};

// 常用API完整URL（供直接使用）
export const API_URLS = {
  LOGIN: buildApiUrl(API_PATHS.LOGIN),
  USER_INFO: buildApiUrl(API_PATHS.USER_INFO),
  DEVICES: buildApiUrl(API_PATHS.DEVICES),
  DEVICE_MONTHLY_REMAINING: (id: string) =>
    buildApiUrl(API_PATHS.DEVICE_MONTHLY_REMAINING(id)),
  EVENTS: buildApiUrl(API_PATHS.EVENTS),
  EVENT_BY_ID: (id: string) => buildApiUrl(API_PATHS.EVENT_BY_ID(id)),
  DASHBOARD_STATS: buildApiUrl(API_PATHS.DASHBOARD_STATS),
  DASHBOARD_EVENTS: buildApiUrl(API_PATHS.DASHBOARD_EVENTS),
  DASHBOARD_HOTSPOTS: buildApiUrl(API_PATHS.DASHBOARD_HOTSPOTS),

  // SIM卡管理API
  SIM_CARDS_STATS: buildApiUrl(API_PATHS.SIM_CARDS_STATS),
  SIM_CARDS_LIST: buildApiUrl(API_PATHS.SIM_CARDS_LIST),
  SIM_CARDS_BILLING_GROUPS: buildApiUrl(API_PATHS.SIM_CARDS_BILLING_GROUPS),
  SIM_CARD_DETAIL: (iccid: string) =>
    buildApiUrl(API_PATHS.SIM_CARD_DETAIL(iccid)),
  SIM_CARD_USAGE: (iccid: string) =>
    buildApiUrl(API_PATHS.SIM_CARD_USAGE(iccid)),
  SIM_CARD_SYNC: (iccid: string) => buildApiUrl(API_PATHS.SIM_CARD_SYNC(iccid)),

  // OTA升级管理API
  OTA: buildApiUrl(API_PATHS.OTA),
  OTA_FIRMWARE: buildApiUrl(API_PATHS.OTA_FIRMWARE),
  OTA_FIRMWARE_UPLOAD: buildApiUrl(API_PATHS.OTA_FIRMWARE_UPLOAD),
  OTA_DEVICES: buildApiUrl(API_PATHS.OTA_DEVICES),
  OTA_UPGRADE: buildApiUrl(API_PATHS.OTA_UPGRADE),
  OTA_AUDIO: buildApiUrl(API_PATHS.OTA_AUDIO),
  OTA_AUDIO_UPLOAD: (audioType: string) =>
    buildApiUrl(API_PATHS.OTA_AUDIO_UPLOAD(audioType)),

  TENANTS: buildApiUrl(API_PATHS.TENANTS),
  TENANT_BY_ID: (id: number | string) =>
    buildApiUrl(API_PATHS.TENANT_BY_ID(id)),
  TENANT_APIKEY_CREATE: (tenantId: number | string) =>
    buildApiUrl(API_PATHS.TENANT_APIKEY_CREATE(tenantId)),
  TENANT_APIKEYS: (tenantId: number | string) =>
    buildApiUrl(API_PATHS.TENANT_APIKEYS(tenantId)),
  TENANT_APIKEY_STATUS: (
    tenantId: number | string,
    apiKeyId: number | string
  ) => buildApiUrl(API_PATHS.TENANT_APIKEY_STATUS(tenantId, apiKeyId)),
  TENANT_APIKEY_DELETE: (tenantId: number | string, keyId: number | string) =>
    buildApiUrl(API_PATHS.TENANT_APIKEY_DELETE(tenantId, keyId)),
};

interface Config {
  httpsUrl: string;
  apiBaseUrl: string;
  apiPaths: typeof API_PATHS;
  apiUrls: typeof API_URLS;
  buildApiUrl: typeof buildApiUrl;
  websocket: {
    contactUrl: string;
    deviceUrl: string;
  };
  wssUrl: string;
  requireLogin: boolean;
}

export default {
  // 所有 HTTP API 请求通过 Nginx 代理到配置服务
  httpsUrl: protocol + "//" + BASE_URL,

  // API相关配置
  apiBaseUrl: API_BASE_URL,
  apiPaths: API_PATHS,
  apiUrls: API_URLS,
  buildApiUrl,

  // WebSocket 连接路径配置
  websocket: {
    // 联络员 WebSocket 连接（通过 Nginx 代理到业务服务）
    contactUrl: wsProtocol + BASE_URL + "/ws/contact",
    // 设备 WebSocket 连接（通过 Nginx 代理到业务服务）
    deviceUrl: wsProtocol + BASE_URL + "/ws/device",
  },

  // 向后兼容的 WebSocket URL（默认为联络员连接）
  wssUrl: wsProtocol + BASE_URL + "/ws/contact",

  // 登录绕过开关：设置为false时可以直接访问UI界面，无需登录验证
  requireLogin: true,
} as Config;
